import type {
  SimpleFilter,
  SimpleFilterDefinition,
} from '@/sections/insights/queryBuilder/entities/types';

import { NodeFilter } from './NodeFilter';

interface NodeFiltersListProps {
  simpleFilters: SimpleFilter[];
  editable: boolean;
  isLoadingFields: boolean;
  filterDefinitions: SimpleFilterDefinition[] | null;
  onFilterChange: (index: number, updatedFilter: Partial<SimpleFilter>) => void;
  onFilterDelete: (index: number) => void;
}

/**
 * NodeFiltersList - Renders list of filter rows with field/operator/value dropdowns
 * Handles loading states, empty states, and read-only fallback
 */
export const NodeFiltersList = ({
  simpleFilters,
  editable,
  isLoadingFields,
  filterDefinitions,
  onFilterChange,
  onFilterDelete,
}: NodeFiltersListProps) => {
  // Loading state
  if (isLoadingFields) {
    return (
      <div className="text-sm text-gray-500">Loading field options...</div>
    );
  }

  // No filters state
  if (simpleFilters.length === 0) {
    return (
      <div className="text-chariot-text-secondary text-sm">
        No filters applied
      </div>
    );
  }

  // Strict requirement: entity must have SimpleFieldDefinition
  if (!filterDefinitions && !isLoadingFields && simpleFilters.length > 0) {
    console.error(
      '[QueryBuilder] Entity missing SimpleFieldDefinition. Filters cannot be displayed.',
      'This indicates incomplete Simple Fields implementation.'
    );
    return (
      <div className="text-sm text-red-600">
        Error: Entity missing field definitions. Cannot display filters.
      </div>
    );
  }

  // Filter display using NodeFilter component
  return (
    <div className="space-y-1">
      {simpleFilters.map((simpleFilter, idx) => (
        <NodeFilter
          key={idx}
          simpleFilter={simpleFilter}
          index={idx}
          editable={editable}
          filterDefinitions={filterDefinitions}
          onFilterChange={onFilterChange}
          onFilterDelete={onFilterDelete}
        />
      ))}
    </div>
  );
};
